import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import { InternalPage } from 'theSource/components';
import { PsRadioTuner, PsCheckboxTuner } from 'stencilSetup';
import { BottomNavigation } from 'enrollment/components';
import { useEnrollmentProfileQuery } from 'enrollment/queries';
import {
  useEnrollmentContextQuery,
  useConfigurationQuery,
} from 'theSource/queries';

import './OliveAdvisor.css';

export const Introduction = () => {
  const { t } = useTranslation('oliveAdvisor');
  const navigate = useNavigate();
  const { subscriber } = useEnrollmentProfileQuery({ lumosEnabled: true });

  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;
  const { enrollmentContext: enrollmentContextResponse } =
    useEnrollmentContextQuery({
      enabled: true,
      apiHost: String(lumosApiHost),
    });
  const subscriberId = subscriber?.data?.id.value;

  const orgPlanYearId = enrollmentContextResponse?.data[0].org_plan_year_id;

  type FormData = {
    employee_budget: string | null;
    emergency_fund_available: boolean;
    medical_planning_pregnancy: boolean;
    medical_planning_medical_procedure: boolean;
    medical_recurring_prescriptions: boolean;
    medical_chronic_conditions: boolean;
    dental_planned_procedure: boolean | null;
    vision_planned_procedure: boolean | null;
  };

  // State variables for form values
  const [formData, setFormData] = useState<FormData>({
    employee_budget: null,
    emergency_fund_available: false,
    medical_planning_pregnancy: false,
    medical_planning_medical_procedure: false,
    medical_recurring_prescriptions: false,
    medical_chronic_conditions: false,
    dental_planned_procedure: null,
    vision_planned_procedure: null,
  });

  // Fetch existing data and populate form fields on mount
  useEffect(() => {
    const fetchSurveyResponses = async () => {
      if (!subscriberId || !orgPlanYearId) {
        return;
      } // Ensure subscriberId is available
      try {
        const response = await axios.get(
          `/olive_advisor/survey_response?subscriber_id=${subscriberId}&&org_plan_year_id=${orgPlanYearId}`
        );
        if (response.data?.data) {
          // Access the nested "data" object
          setFormData(response.data.data); // Set formData with the correct data
        }
      } catch (error) {
        console.error('Error fetching survey response:', error);
      }
    };

    fetchSurveyResponses();
  }, [subscriberId, orgPlanYearId]);

  //Handle form updates for individual inputs
  const handleInputChange = (
    field: keyof FormData,
    value: FormData[keyof FormData]
  ) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const handleNextPage = async () => {
    const surveyResponses = {
      employee_budget:
        (
          document.querySelector(
            'input[name="employee_budget"]:checked'
          ) as HTMLInputElement
        )?.value ?? null,
      emergency_fund_available:
        (
          document.querySelector(
            'input[name="emergency_fund_available"]'
          ) as HTMLInputElement
        )?.checked ?? false,
      medical_planning_pregnancy:
        (
          document.getElementById(
            'medical_planning_pregnancy'
          ) as HTMLInputElement
        )?.checked ?? false,
      medical_planning_medical_procedure:
        (
          document.getElementById(
            'medical_planning_medical_procedure'
          ) as HTMLInputElement
        )?.checked ?? false,
      medical_recurring_prescriptions:
        (
          document.getElementById(
            'medical_recurring_prescriptions'
          ) as HTMLInputElement
        )?.checked ?? false,
      medical_chronic_conditions:
        (
          document.getElementById(
            'medical_chronic_conditions'
          ) as HTMLInputElement
        )?.checked ?? false,
      dental_planned_procedure:
        (
          document.querySelector(
            'input[name="dental_planned_procedure"]'
          ) as HTMLInputElement
        )?.checked ?? false,
      vision_planned_procedure:
        (
          document.querySelector(
            'input[name="vision_planned_procedure"]'
          ) as HTMLInputElement
        )?.checked ?? false,
    };

    const data = {
      subscriber_id: subscriberId,
      org_plan_year_id: orgPlanYearId,
      survey_responses: surveyResponses,
    };

    try {
      await axios.put('/olive_advisor/survey_response', data);
      navigate('/enrollment/benefits');
    } catch (error) {
      console.error('Error submitting survey response:', error);
    }
  };

  return (
    <InternalPage testId="olive-advisor-introduction">
      <h1 className="mx-4 mx-md-0">{t('h1')}</h1>
      <div className="card py-5 ">
        <div id="olive-advisor-intro" className="blue-info-box">
          <div className="d-flex">
            <div className="olive-container mr-2 mr-md-3">
              <div
                className="olive-icon"
                aria-hidden="true"
                role="presentation"
              ></div>
            </div>
            <div>
              <h2>{t('oliveAdvisor')}</h2>
              <p
                dangerouslySetInnerHTML={{ __html: t('introduction') }}
                className="mb-0"
              />
            </div>
          </div>
        </div>

        <section className="my-5">
          <h2>{t('welcome')}</h2>
          <p className="mb-4">{t('welcome1')}</p>
          <p>{t('welcome2')}</p>
        </section>

        <hr className="darker" />
        <section className="mt-5">
          <h3>{t('someQuestions')}</h3>
          <p className="mb-4">{t('diveIn')}</p>
          <form>
            <div className="border-top py-4">
              <fieldset className="olive-question">
                <legend>{t('yourBudget')}</legend>
                <PsRadioTuner
                  inputId="low"
                  name="employee_budget"
                  label={t('low')}
                  value="low"
                  checked={formData.employee_budget === 'low'}
                  onChange={() => handleInputChange('employee_budget', 'low')}
                />
                <PsRadioTuner
                  inputId="moderate"
                  name="employee_budget"
                  label={t('moderate')}
                  value="moderate"
                  checked={formData.employee_budget === 'moderate'}
                  onChange={() =>
                    handleInputChange('employee_budget', 'moderate')
                  }
                />
                <PsRadioTuner
                  inputId="high"
                  name="employee_budget"
                  label={t('high')}
                  value="high"
                  checked={formData.employee_budget === 'high'}
                  onChange={() => handleInputChange('employee_budget', 'high')}
                />
              </fieldset>
            </div>
            <div className="border-top py-4">
              <fieldset className="olive-question">
                <legend>{t('expenses.medical.legend')}</legend>
                <PsCheckboxTuner
                  inputId="medical_planning_pregnancy"
                  name="planned-medical"
                  label={t('expenses.medical.pregnancy')}
                  checked={formData.medical_planning_pregnancy}
                  onChange={() =>
                    handleInputChange(
                      'medical_planning_pregnancy',
                      !formData.medical_planning_pregnancy
                    )
                  }
                />
                <PsCheckboxTuner
                  inputId="medical_planning_medical_procedure"
                  name="planned-medical"
                  label={t('expenses.medical.procedure')}
                  checked={formData.medical_planning_medical_procedure}
                  onChange={() =>
                    handleInputChange(
                      'medical_planning_medical_procedure',
                      !formData.medical_planning_medical_procedure
                    )
                  }
                />
                <PsCheckboxTuner
                  inputId="medical_recurring_prescriptions"
                  name="planned-medical"
                  label={t('expenses.medical.prescriptions')}
                  checked={formData.medical_recurring_prescriptions}
                  onChange={() =>
                    handleInputChange(
                      'medical_recurring_prescriptions',
                      !formData.medical_recurring_prescriptions
                    )
                  }
                />
                <PsCheckboxTuner
                  inputId="medical_chronic_conditions"
                  name="planned-medical"
                  label={t('expenses.medical.chronic')}
                  checked={formData.medical_chronic_conditions}
                  onChange={() =>
                    handleInputChange(
                      'medical_chronic_conditions',
                      !formData.medical_chronic_conditions
                    )
                  }
                />
              </fieldset>
            </div>
            <div className="border-top py-4">
              <fieldset className="olive-question">
                <legend>{t('expenses.dental.legend')}</legend>
                <div className="d-inline-block mr-4">
                  <PsRadioTuner
                    inputId="dental-yes"
                    name="dental_planned_procedure"
                    label={t('yes')}
                    checked={formData.dental_planned_procedure === true}
                    onChange={() =>
                      handleInputChange('dental_planned_procedure', true)
                    }
                  />
                </div>

                <div className="d-inline-block">
                  <PsRadioTuner
                    inputId="dental-no"
                    name="dental_planned_procedure"
                    label={t('no')}
                    checked={formData.dental_planned_procedure === false}
                    onChange={() =>
                      handleInputChange('dental_planned_procedure', false)
                    }
                  />
                </div>
              </fieldset>
            </div>
            <div className="border-top py-4">
              <fieldset className="olive-question">
                <legend>{t('expenses.vision.legend')}</legend>
                <div className="d-inline-block mr-4">
                  <PsRadioTuner
                    inputId="vision-yes"
                    name="vision_planned_procedure"
                    label={t('yes')}
                    value="yes"
                    checked={formData.vision_planned_procedure === true}
                    onChange={() =>
                      handleInputChange('vision_planned_procedure', true)
                    }
                  />
                </div>

                <div className="d-inline-block">
                  <PsRadioTuner
                    inputId="vision-no"
                    name="vision_planned_procedure"
                    label={t('no')}
                    value="no"
                    checked={formData.vision_planned_procedure === false}
                    onChange={() =>
                      handleInputChange('vision_planned_procedure', false)
                    }
                  />
                </div>
              </fieldset>
            </div>
            <div className="border-top py-4">
              <fieldset className="olive-question">
                <legend>
                  Do you have $2,500 to $5,000 set aside in an emergency fund
                  for unexpected medical expenses or illnesses?
                </legend>
                <div className="d-inline-block mr-4">
                  <PsRadioTuner
                    inputId="emergencyfund-yes"
                    name="emergency_fund_available"
                    label={t('yes')}
                    value={true}
                    checked={formData.emergency_fund_available === true}
                    onChange={() =>
                      handleInputChange('emergency_fund_available', true)
                    }
                  />
                </div>

                <div className="d-inline-block">
                  <PsRadioTuner
                    inputId="emergencyfund-no"
                    name="emergency_fund_available"
                    label={t('no')}
                    value={false}
                    checked={formData.emergency_fund_available === false}
                    onChange={() =>
                      handleInputChange('emergency_fund_available', false)
                    }
                  />
                </div>
              </fieldset>
            </div>
          </form>
        </section>
        <div className="blue-info-box d-flex py-3">
          <i
            className="ph ph-shield-check mr-2_5"
            aria-hidden="true"
            role="presentation"
          ></i>

          <div>
            <b>{t('privacy')}</b>
            <p className="body2 mt-2 mb-0">{t('privacyStatement')}</p>
          </div>
        </div>
      </div>
      <BottomNavigation
        translatedPage="oliveAdvisor"
        completePages={['profile', 'family', 'questionnaire']}
        onPage="olive-advisor"
        onClickEvent={handleNextPage}
      />
    </InternalPage>
  );
};
